import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { signInHref, SIGNED_IN, useSession } from '../session.js'
import { LOADING, MISCONFIGURED, READY, UNAVAILABLE, titlesOf, useWatchArchive, yourArchiveEntry } from './watchArchive.js'
import {
  APPLIED,
  KIND_LINK_SEQUEL,
  KIND_LIST_TITLE,
  KIND_LOG,
  REFUSED,
  TARGET_STOPPED,
  TARGET_WATCHED,
  TARGET_WATCHING,
  UNREACHABLE,
  UNSENT,
  actionSentence,
  linkCommand,
  logCommand,
  logTargetForSearchHit,
  useWatchRequestBatch,
} from './watchRequests.js'
import { PosterSlot } from './WatchPanel.jsx'

// V2-6f2 — "Log the whole series." Jordan, 2026-09-24, verbatim (brief §0):
// *"If I search 'mob psycho', which season should I select? … All of the
// seasons are out, I've seen all of them, and would need to bind them
// together somehow."* From an AniList search result, this shows the whole
// season chain (`GET /api/watch/series`, V2-6f1) as a checklist and turns
// one tap into a `watch.log` per ticked season plus a `watch.link_sequel`
// per consecutive pair across the whole chain.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 ROUND 2: A TITLE MATCH FAILS IN THE DANGEROUS DIRECTION — FIXED BY
//    PUBLISHING `source` ON THE ARCHIVE, THE BRIEF'S OWN ASK, PROPERLY
// ═══════════════════════════════════════════════════════════════════════════
//
// Round 1 of this slice matched a checklist season to an archive row by
// `kind === 'anime'` + exact TITLE STRING, because `GET /api/watch/archive`
// (`utils/watch_projection.py::_archive_title_view`) published no provider
// id at the time — measured, and recorded at length in this slice's own
// report. That round argued the residual risk "runs safe, not dangerous":
// a title collision could only produce a false positive. **That argument
// missed the case the red rule exists for.** The archive's `title` is
// whatever the bot resolved WHEN THE CATALOG ROW WAS CREATED
// (`cogs/watch/providers.py::_anilist_title`); the checklist's season title
// is AniList's CURRENT title for that id, resolved independently by the
// trip service on every open. If AniList's own title text for that id has
// changed AT ALL since the row was created — a genuine, ordinary occurrence
// on AniList, not a hypothetical — the two strings disagree, the site's
// title match MISSES a season this member logged WITH A DATE, and the
// batch sends it a dateless `watch.log`. `set_completion_date` overwrites.
// That is exactly the loss ADR-0009 / brief §2.3 forbid, reached by the
// exact mechanism this file's round-1 comment (wrongly) argued could not
// produce it.
//
// ✅ THE FIX: `_archive_title_view` now publishes `source: {provider, id}`
// (V2-6f2 round 2) — the brief's own original ask, built properly this
// time, because the reason round 1 gave for not doing it (a bot-side file
// touch was out of scope) turned out to be the wrong trade against a real
// data-loss bug. `findLoggedArchiveEntry` below matches on
// `(source.provider, source.id)` alone — an id never changes, is never
// re-resolved, and cannot drift the way a rendered title can. The TITLE
// comparison is gone entirely, not weakened; there is no longer a
// "residual risk" paragraph to write, because there is no comparison left
// that a rename, a re-fetch, or a translation preference can fool.
//
// ⚠️ FAIL CLOSED WHILE THE FIELD IS ABSENT. The site can deploy before the
// bot republishes an archive object carrying `source` on every row (the
// bot's own next sweep, not instant on its own deploy) — so for a real
// window, a live archive blob can still be the OLD shape, with no `source`
// on any row at all. `archiveSupportsSourceMatch` below detects exactly
// that: if EVERY row in the fetched archive lacks a well-formed `source`,
// this checklist cannot clear ANY season, so every otherwise-loggable
// season reads `ROW_UNVERIFIED` — never a guess, and never treated as "not
// logged" merely because the field was not there to check. An EMPTY
// archive vacuously "supports" the check (`Array.prototype.every` on `[]`
// is `true`): there are no rows to get wrong either way, and treating it
// as unverified would block a brand-new member from ever using this
// checklist for no reason. `every`, not `some`, so a hypothetical PARTIAL
// rollout (some rows carrying `source`, some not — not something the bot's
// two record-creation paths can actually produce, since both have set
// `source` unconditionally since before this feature existed, but not
// provably impossible from the site's side) fails closed rather than
// silently trusting whichever rows happen to answer.

// ═══════════════════════════════════════════════════════════════════════════
// 🔴 P6-293: A SEASON YOU'RE WATCHING OR HAVE STOPPED WAS SILENTLY
//    PRE-TICKED, AND THERE WAS NO ONE-TAP "LINK ONLY, NOTHING WATCHED"
// ═══════════════════════════════════════════════════════════════════════════
//
// Jordan asked how to log a whole anime both when he is already partway
// through its newest season and when he has seen none of it at all (brief
// §0). Answering that surfaced two rough edges this checklist shipped with:
//
// (1) `rowKindFor` makes every FINISHED/CANCELLED season `ROW_TICKABLE` the
// moment it is not already logged `watched` — including one this member's
// own archive entry says is `watching` or `stopped` right now. The
// default-tick effect then ticks every `ROW_TICKABLE` row, so a season the
// member has not actually finished gets pre-ticked, and one tap sends it a
// dateless `watch.log` — `set_completion_date` really does move
// `watching`/`stopped` straight to `watched`, overwriting nothing (there
// was no date to lose) but silently declaring the member done with a
// season they are still mid-way through.
//
// ✅ THE FIX: such a row STAYS `ROW_TICKABLE` — the member may really have
// finished it since their last update, and nothing here should take that
// choice away — but it is excluded from the default-tick set, and its row
// says so in plain words (`inProgressStatusForSeason`/`inProgressWord`,
// below): "You’re watching this" / "You stopped this". Matched by
// `(source.provider, source.id)`, the IDENTICAL comparison
// `findLoggedArchiveEntry` already uses — factored into `matchArchiveRow`
// so there is exactly one such comparison in this file, never two written
// separately and assumed to agree (CLAUDE.md gotcha 21).
//
// (2) `buildBatchAsks` already sends every link in the chain regardless of
// what is ticked ("the chain's order is a fact about the titles," brief,
// verbatim) — but there was no one-tap way to send ONLY the links. A member
// who has watched none of a chain had to untick every row by hand to avoid
// logging seasons they have not seen.
//
// ✅ THE FIX: a secondary "Link only (nothing watched)" button sends
// exactly the already-computed link asks (`asks` filtered to
// `KIND_LINK_SEQUEL` — no second ask-builder), shown whenever at least one
// season is ticked. When NOTHING is ticked, the PRIMARY button already
// sends links only (`buildBatchAsks` produces zero log asks for an empty
// `ticked` set — this was already true before this slice), so it is simply
// relabelled "Link the chain" rather than "Log & link the chain," which
// would otherwise promise a log it does not send. The finished summary
// follows the same rule: "Linked N" when the SUBMITTED batch carried no
// `watch.log` item at all (`hasAnyLogItems`, read off `batch.items` —
// never off `ticked`, which stays checked after a link-only run and would
// answer the wrong question), "Logged N · Linked M" otherwise, unchanged.

// ═══════════════════════════════════════════════════════════════════════════
// P6-298c — "Add the whole series (nothing watched)"
// ═══════════════════════════════════════════════════════════════════════════
//
// Jordan, 2026-09-29, verbatim: *"It needs to be simple for me to just add a
// whole series of a show (without saying I have watched all of it) to the
// server rotation, and then I can manipulate it with other commands."*
// `watch.list_title` (`cogs/watch/requests.py`) is the missing write: create-
// if-absent, then list, with nobody marked watching. This checklist is
// already the one place a whole season chain is on screen at once, so its
// THIRD button sends one `watch.list_title` per season — EVERY season,
// whatever its airing status or tick state, never gated by `rowKindFor`
// (unlike the log asks above: getting a still-airing season onto the
// rotation is the whole point, not a special case) — plus the SAME
// `watch.link_sequel` per consecutive pair the other two buttons already
// send (`buildChainLinkAsks`, factored out below so there is one link-loop,
// not three). No `watch.log` ask is ever built here, whatever is ticked —
// `buildWholeSeriesAsks` never reads `ticked` at all.
//
// Sits beside "Link only (nothing watched)" — both are "nothing watched"
// actions — but unlike that button, this one is not conditional on any tick
// state (there is nothing to untick your way into: it always means "every
// season"), and unlike the whole PRIMARY-button surface, it does not need
// the archive to have settled at all: `set_listed`/`add_catalog_title` are
// idempotent no-ops on anything already in the desired state, so there is no
// "already logged" question this button could get wrong the way logging
// could. See `SeriesChecklistBody`'s own render for exactly where it sits.

// ═══════════════════════════════════════════════════════════════════════════
// The series lookup — `GET /api/watch/series?anilist=<id>` (V2-6f1)
// ═══════════════════════════════════════════════════════════════════════════

const SERIES_ENDPOINT = '/api/watch/series'

export const SERIES_LOADING = 'loading'
export const SERIES_READY = 'ready'
export const SERIES_SIGNED_OUT = 'signed-out'
export const SERIES_NOT_FOUND = 'not-found'
export const SERIES_LIMITED = 'limited'
export const SERIES_ERROR = 'error'

const SERIES_GENERIC_ERROR = "Couldn't fetch the whole series. Try again in a moment."
const SERIES_RATE_LIMITED_FALLBACK = 'That’s a lot of season lookups at once. Give it a minute.'
const SERIES_NOT_FOUND_FALLBACK = 'No title with that id.'

function loadingSeries() {
  return { status: SERIES_LOADING }
}

/** Every season entry defensively re-checked at this seam — `watchArchive.js`'s
 * own `titlesOf` states the identical rule for its object: a Function two
 * hops from the platform service is not somewhere to trust a shape on
 * faith, even though `SeriesSeasonRead` is Pydantic-validated on its way
 * out. Anything malformed is dropped rather than crashing the render. */
function seasonsOf(body) {
  const list = body?.seasons
  if (!Array.isArray(list)) return null
  return list.filter(
    (season) =>
      season &&
      typeof season === 'object' &&
      typeof season.provider === 'string' &&
      Number.isInteger(season.id) &&
      typeof season.title === 'string',
  )
}

/**
 * `anilistId`'s whole season chain, fetched once. `useTitleSearch`'s own
 * shape (debounced, re-asked on every keystroke) is the wrong one here —
 * this fetches exactly once per id, the same single-fetch-on-mount
 * discipline `watchArchive.js::useWatchArchive` already uses for a
 * "load it, then leave it" surface.
 */
export function useSeriesLookup(anilistId, fetchImpl = globalThis.fetch) {
  const [state, setState] = useState(loadingSeries)

  useEffect(() => {
    let cancelled = false
    setState(loadingSeries())

    ;(async () => {
      let response
      try {
        response = await fetchImpl(`${SERIES_ENDPOINT}?anilist=${encodeURIComponent(anilistId)}`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        })
      } catch {
        if (cancelled) return
        setState({ status: SERIES_ERROR, message: SERIES_GENERIC_ERROR })
        return
      }
      if (cancelled) return

      if (response.status === 401) {
        setState({ status: SERIES_SIGNED_OUT })
        return
      }
      if (response.status === 404) {
        const text = await response.text().catch(() => '')
        setState({ status: SERIES_NOT_FOUND, message: text.trim() || SERIES_NOT_FOUND_FALLBACK })
        return
      }
      if (response.status === 429) {
        const text = await response.text().catch(() => '')
        setState({ status: SERIES_LIMITED, message: text.trim() || SERIES_RATE_LIMITED_FALLBACK })
        return
      }
      if (!response.ok) {
        setState({ status: SERIES_ERROR, message: SERIES_GENERIC_ERROR })
        return
      }

      const body = await response.json().catch(() => null)
      const seasons = seasonsOf(body)
      if (!seasons) {
        setState({ status: SERIES_ERROR, message: SERIES_GENERIC_ERROR })
        return
      }
      setState({
        status: SERIES_READY,
        complete: body.complete === true,
        reason: typeof body.reason === 'string' ? body.reason : null,
        seasons,
      })
    })()

    return () => {
      cancelled = true
    }
  }, [anilistId, fetchImpl])

  return state
}

// ═══════════════════════════════════════════════════════════════════════════
// Row logic — tickable / not tickable / logged / unverified
// ═══════════════════════════════════════════════════════════════════════════

/** AniList's own vocabulary (brief §2.3) that means "this has aired in
 * full" — the only statuses this checklist will ever offer a checkbox for.
 * A season you haven't finished can't be logged as finished. */
const LOGGABLE_STATUSES = new Set(['FINISHED', 'CANCELLED'])

export function isLoggableStatus(status) {
  return LOGGABLE_STATUSES.has(status)
}

/** The plain word for a season with no checkbox — brief §2.3's own two
 * examples, extended to the AniList statuses it names but does not word
 * (`HIATUS`) and to anything this build has never heard of (`null`, or a
 * future AniList value). A lookup, never an interpolation —
 * `functions/_lib/respond.js::STAGE_QUESTION`'s own rule, restated for a
 * vocabulary this checklist did not choose. */
const STATUS_WORD = Object.freeze({
  RELEASING: 'Still airing',
  NOT_YET_RELEASED: 'Not out yet',
  HIATUS: 'On hiatus',
})

export function statusWord(status) {
  return Object.hasOwn(STATUS_WORD, status) ? STATUS_WORD[status] : 'Status unknown'
}

/** A well-formed `source` on an archive row — round 2's own published
 * field. `id` is checked for presence rather than truthiness alone (an id
 * of `0` is falsy but would be a legitimate, if unusual, provider id). */
function hasSource(title) {
  const source = title?.source
  return !!source && typeof source === 'object' && source.id !== null && source.id !== undefined
}

/**
 * Does the FETCHED archive, as a whole, carry `source` on every row it has?
 * A property of the whole payload (was this JSON built by pre- or
 * post-round-2 code), never of one row in isolation — see the module
 * header's "FAIL CLOSED WHILE THE FIELD IS ABSENT" section. `every` on an
 * empty array is vacuously `true`: nothing to match, so nothing to get
 * wrong either way.
 */
export function archiveSupportsSourceMatch(archiveTitles) {
  return archiveTitles.every((title) => hasSource(title))
}

/**
 * The archive row matching `season` by `(source.provider, source.id)` — the
 * ONE place this checklist matches a season to an archive row.
 * `findLoggedArchiveEntry` and `inProgressStatusForSeason` (P6-293, below)
 * both call this rather than each re-deriving the same comparison —
 * CLAUDE.md gotcha 21: two questions about the same row must share one
 * matching function, never two written separately and assumed to agree.
 * `null` for no match (including a malformed `season`).
 */
function matchArchiveRow(season, archiveTitles) {
  if (!season || typeof season !== 'object') return null
  return (
    archiveTitles.find(
      (title) => hasSource(title) && title.source.provider === season.provider && title.source.id === season.id,
    ) ?? null
  )
}

/**
 * Does `season` match an archive row this member has already logged
 * (`watched`, dated or not — see the module header on why "logged at all"
 * rather than "logged with a date specifically" is the simpler and safer
 * line to draw)? Matches on `(source.provider, source.id)` alone — round
 * 2's fix; see the module header for why a title comparison was a real
 * false-negative risk, not merely an approximation. `null` when there is
 * no match, or a match this member has not watched. Callers must check
 * `archiveSupportsSourceMatch` first (`rowKindFor` does) — this function
 * does not itself distinguish "no match" from "could not check."
 */
export function findLoggedArchiveEntry(season, archiveTitles, userId) {
  const hit = matchArchiveRow(season, archiveTitles)
  if (!hit) return null
  const mine = yourArchiveEntry(hit, userId)
  return mine && mine.status === TARGET_WATCHED ? hit : null
}

/**
 * P6-293: this member's own status on `season`'s matching archive row, when
 * it is `watching` or `stopped` — the "you're partway through this" case
 * the module header's rule (1) exists for. Shares `matchArchiveRow` with
 * `findLoggedArchiveEntry` rather than a second lookup (gotcha 21, same as
 * above). `null` for no match, no entry for this member, or any OTHER
 * status — including `watched`, which is `findLoggedArchiveEntry`'s own
 * question, never this one's. Callers reach for this only once `rowKindFor`
 * has already decided the row is `ROW_TICKABLE` (which itself already
 * required `archiveSupportsSourceMatch`), so there is no separate
 * fail-closed check to repeat here.
 */
export function inProgressStatusForSeason(season, archiveTitles, userId) {
  const hit = matchArchiveRow(season, archiveTitles)
  if (!hit) return null
  const mine = yourArchiveEntry(hit, userId)
  if (!mine) return null
  return mine.status === TARGET_WATCHING || mine.status === TARGET_STOPPED ? mine.status : null
}

/** The plain-word note for a `ROW_TICKABLE` row this member is already
 * partway through — brief §2 rule 1's own two examples, in this module's
 * established curly-apostrophe convention (every other member-facing
 * sentence here uses one). `null` for anything else, including `null`
 * itself — callers only reach for this once `inProgressStatusForSeason` has
 * already returned non-null. */
export function inProgressWord(status) {
  if (status === TARGET_WATCHING) return 'You’re watching this'
  if (status === TARGET_STOPPED) return 'You stopped this'
  return null
}

export const ROW_NOT_TICKABLE = 'not-tickable'
export const ROW_UNVERIFIED = 'unverified'
export const ROW_LOGGED = 'logged'
export const ROW_TICKABLE = 'tickable'

/**
 * One season's row state. `archiveState` is `useWatchArchive`'s own
 * `state` (`LOADING`/`READY`/`UNAVAILABLE`/`MISCONFIGURED`) — anything
 * other than `READY` means this checklist cannot clear ANY season at all,
 * so every otherwise-loggable row reads `ROW_UNVERIFIED` rather than
 * guessing either way (brief §2.3: "fail closed. Say so in the panel
 * rather than guessing"). The identical `ROW_UNVERIFIED` fallback covers
 * an archive that loaded fine but predates round 2's `source` field
 * (`archiveSupportsSourceMatch`) — two different reasons this checklist
 * cannot answer, one outcome either way.
 */
export function rowKindFor(season, archiveState, archiveTitles, userId) {
  if (!isLoggableStatus(season.status)) return ROW_NOT_TICKABLE
  if (archiveState !== READY) return ROW_UNVERIFIED
  if (!archiveSupportsSourceMatch(archiveTitles)) return ROW_UNVERIFIED
  return findLoggedArchiveEntry(season, archiveTitles, userId) ? ROW_LOGGED : ROW_TICKABLE
}

// ═══════════════════════════════════════════════════════════════════════════
// The batch — one `watch.log` per ticked season, one `watch.link_sequel`
// per consecutive pair across the WHOLE chain
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Brief §2.4. `ticked` is a `Set` of season ids currently checked. Links
 * cover the whole chain regardless of ticking, airing status, or anything
 * else about this member — "the chain's order is a fact about the titles"
 * (brief, verbatim) — so they are built from `seasons` directly, never
 * filtered by `rowKindFor`.
 *
 * Each ask carries its own `label` and Discord `command` fallback
 * (`logCommand`/`linkCommand`, reused rather than a second builder) so the
 * component can render either without re-deriving them from `target`.
 */
/**
 * The `watch.link_sequel` half of a batch — one ask per consecutive pair
 * across the WHOLE chain, unconditionally ("the chain's order is a fact
 * about the titles," brief §2.4). Factored out of `buildBatchAsks` so
 * `buildWholeSeriesAsks` (P6-298c) shares this loop rather than a second
 * copy of it — CLAUDE.md gotcha 1's *copies* axis, one file over.
 */
function buildChainLinkAsks(seasons) {
  const asks = []
  for (let i = 0; i < seasons.length - 1; i += 1) {
    const from = seasons[i]
    const to = seasons[i + 1]
    const fromTarget = logTargetForSearchHit(from)
    const toTarget = logTargetForSearchHit(to)
    if (!fromTarget || !toTarget) continue
    asks.push({
      key: `link:${from.id}->${to.id}`,
      kind: KIND_LINK_SEQUEL,
      target: fromTarget,
      payload: { next: toTarget },
      label: `${from.title} → ${to.title}`,
      command: linkCommand(from.title, to.title),
    })
  }
  return asks
}

export function buildBatchAsks(seasons, ticked, archiveState, archiveTitles, userId) {
  const asks = []
  for (const season of seasons) {
    if (rowKindFor(season, archiveState, archiveTitles, userId) !== ROW_TICKABLE) continue
    if (!ticked.has(season.id)) continue
    const target = logTargetForSearchHit(season)
    if (!target) continue
    asks.push({
      key: `log:${season.id}`,
      kind: KIND_LOG,
      target,
      payload: { watched_when: { precision: 'none', value: null } },
      label: season.title,
      command: logCommand(season.title, null),
    })
  }
  asks.push(...buildChainLinkAsks(seasons))
  return asks
}

/**
 * P6-298c: "Add the whole series (nothing watched)" — one `watch.list_title`
 * per season, unconditionally (every season in the chain, whatever its
 * airing status or tick state — getting a series onto the rotation is the
 * whole point, not a special case for finished seasons only), plus the SAME
 * `watch.link_sequel` per consecutive pair `buildBatchAsks` already sends
 * (`buildChainLinkAsks`, shared rather than rebuilt). Never reads `ticked`
 * at all, and never builds a `watch.log` ask — brief §2.2: "No watch.log,
 * whatever is ticked."
 *
 * No `command` fallback on the `list:` asks (unlike `log:`/`link:`): there
 * is no `/watch` command with this exact effect (list with nobody
 * watching) for `itemStatusText`'s Unreachable branch to name — the same
 * "decline rather than guess" rule `watchRequests.js::KIND_LIST_TITLE`'s
 * own header states, never a fabricated command (RECONCILIATION §5).
 */
export function buildWholeSeriesAsks(seasons) {
  const asks = []
  for (const season of seasons) {
    const target = logTargetForSearchHit(season)
    if (!target) continue
    asks.push({
      key: `list:${season.id}`,
      kind: KIND_LIST_TITLE,
      target,
      payload: { title: season.title, poster_url: season.poster ?? null },
      label: season.title,
    })
  }
  asks.push(...buildChainLinkAsks(seasons))
  return asks
}

/** "Logged 3 · Linked 4" (or, for the whole-series button, "Listed 3 ·
 * Linked 4" — P6-298c) — brief §2.4's own example. Counts APPLIED items by
 * kind; a still-pending or refused ask is not counted yet, so the numbers
 * only ever grow as the batch settles, never overclaiming before the bot
 * confirms (ADR-0009 D3 / `ActStrip`'s own rule, one seam over). */
export function batchSummary(items) {
  let logged = 0
  let linked = 0
  let listed = 0
  for (const item of items) {
    if (item.status !== APPLIED) continue
    if (item.kind === KIND_LOG) logged += 1
    else if (item.kind === KIND_LINK_SEQUEL) linked += 1
    else if (item.kind === KIND_LIST_TITLE) listed += 1
  }
  return { logged, linked, listed }
}

/**
 * P6-293b: the default ticks JOIN whatever the member has already ticked;
 * they never replace it. A row's checkbox renders in the same commit that
 * makes the archive READY, and the default-tick effect runs after that
 * commit, so a tick landing in between used to be wiped by the effect's
 * `setTicked(next)`. Measured on `main` at `42756b0`: under runner
 * contention the P6-293 test clicked in that gap and its tick vanished.
 * The starting set is empty, so nothing can be un-ticked before the
 * defaults arrive, and a union is the whole rule. Returns a new Set; never
 * mutates either argument.
 */
export function withDefaultTicks(previous, defaults) {
  const merged = new Set(defaults)
  for (const id of previous) merged.add(id)
  return merged
}

export function unsentCount(items) {
  return items.filter((item) => item.status === UNSENT).length
}

/**
 * P6-293: whether the batch that was actually SUBMITTED (`batch.items`)
 * included any `watch.log` ask at all. Read off `items`, never off
 * `ticked` — a ticked box stays checked after "Link only" is pressed (the
 * secondary button sends a filtered subset of `asks`; it never clears a
 * tick), so `ticked.size > 0` would answer the wrong question for exactly
 * the run this function exists to describe.
 */
export function hasAnyLogItems(items) {
  return items.some((item) => item.kind === KIND_LOG)
}

/** P6-298c: whether the batch that was actually SUBMITTED (`batch.items`)
 * included any `watch.list_title` ask at all — the whole-series button's own
 * marker, the identical "read off items, never off ticked" discipline
 * `hasAnyLogItems`'s own header states for its sibling. */
export function hasAnyListItems(items) {
  return items.some((item) => item.kind === KIND_LIST_TITLE)
}

/** "Logged 3 · Linked 4" — brief §2.4's own example — or, when the
 * submitted batch carried no `watch.log` at all (a "Link only" run, or the
 * no-ticks primary button, which sends the identical link-only set), just
 * "Linked N" (P6-293 §2 rule 2: never claim to have logged nothing).
 * P6-298c: the whole-series button's own batch carries `watch.list_title`
 * items instead of `watch.log` ones, so it reads "Listed 3 · Linked 4" —
 * a THIRD, mutually exclusive shape, never both clauses at once, since no
 * single batch this file ever builds mixes `KIND_LOG` and `KIND_LIST_TITLE`
 * asks. */
export function finishedSummaryText(items) {
  const { logged, linked, listed } = batchSummary(items)
  if (hasAnyLogItems(items)) return `Logged ${logged} · Linked ${linked}`
  if (hasAnyListItems(items)) return `Listed ${listed} · Linked ${linked}`
  return `Linked ${linked}`
}

// ═══════════════════════════════════════════════════════════════════════════
// The component
// ═══════════════════════════════════════════════════════════════════════════

/** One item's own compact status line — `actionSentence` reused for the
 * five states it already knows (never a second sentence table), with the
 * one state it does not — `UNSENT`, batch-only, see `watchRequests.js`'s
 * own header — worded here. `null` before an item has been attempted at
 * all (still queued behind an earlier one in the same sequential pass).
 *
 * `UNREACHABLE` also surfaces the item's own Discord mono fallback
 * (`item.command`, built by `buildBatchAsks` from `logCommand`/
 * `linkCommand` — reused, never a second builder) — brief §2.4: "surface
 * it where the Add panel already does," `ActStrip`'s own
 * Unreachable-with-command treatment for a single control, one seam over
 * for a compact per-row line instead of a second full `ActStrip` per item
 * (up to 23 of those would be noise; see the report).
 *
 * 🔴 REACHED ONLY VIA A POLL THAT NEVER SETTLES, NOT VIA A CREATE-TIME
 * FAILURE. `runAsksSequentially` (`watchRequests.js`) maps a create-time
 * `unreachable` to `UNSENT`, deliberately, the SAME "stop the batch, offer
 * a retry" treatment as a 429 (its own module header) — so this item state
 * is reached only when a request WAS accepted (a real id) and polling then
 * exhausted `MAX_CONSECUTIVE_POLL_FAILURES`/`MAX_WAIT_MS` without ever
 * learning its fate, exactly like a single `useWatchRequest` control. */
export function itemStatusText(item) {
  if (!item.status) return null
  if (item.status === UNSENT) return 'Not sent — too many at once.'
  if (item.status === UNREACHABLE && item.command) {
    return `Buttons are unavailable — /watch in Discord still works: ${item.command}`
  }
  return actionSentence(item.status, item.message)
}

function SeriesChecklistBody({ anilistId, onClose, testId }) {
  const location = useLocation()
  const session = useSession()
  const signedIn = session.status === SIGNED_IN
  const archive = useWatchArchive(signedIn)
  const series = useSeriesLookup(anilistId)
  const batch = useWatchRequestBatch()
  const [ticked, setTicked] = useState(() => new Set())
  const defaultsApplied = useRef(false)

  const archiveTitles = useMemo(
    () => (archive.state === READY ? titlesOf(archive.shared) : []),
    [archive.state, archive.shared],
  )
  const userId = session.userId

  // Default-tick every genuinely tickable row EXCEPT one this member's own
  // archive entry says is still `watching` or `stopped` (P6-293: such a
  // season may have finished airing with nobody having marked it so, and
  // pre-ticking it would silently turn "partway through" into "finished"
  // on one tap — module header, rule 1) — and only once the archive has
  // SETTLED (ready, or honestly failed) — ticking against a still-loading
  // archive would tick a row this checklist cannot yet clear, which is the
  // opposite of fail closed.
  useEffect(() => {
    if (defaultsApplied.current) return
    if (series.status !== SERIES_READY) return
    if (archive.state === LOADING) return
    defaultsApplied.current = true
    const next = new Set()
    for (const season of series.seasons) {
      if (rowKindFor(season, archive.state, archiveTitles, userId) !== ROW_TICKABLE) continue
      if (inProgressStatusForSeason(season, archiveTitles, userId)) continue
      next.add(season.id)
    }
    setTicked((previous) => withDefaultTicks(previous, next))
  }, [series.status, series.seasons, archive.state, archiveTitles, userId])

  const asks = useMemo(
    () =>
      series.status === SERIES_READY
        ? buildBatchAsks(series.seasons, ticked, archive.state, archiveTitles, userId)
        : [],
    [series.status, series.seasons, ticked, archive.state, archiveTitles, userId],
  )
  // P6-298c: "Add the whole series (nothing watched)" — independent of
  // `ticked`/archive state entirely (see `buildWholeSeriesAsks`'s own
  // header), so this is its own memo rather than a filter of `asks` above.
  const wholeSeriesAsks = useMemo(
    () => (series.status === SERIES_READY ? buildWholeSeriesAsks(series.seasons) : []),
    [series.status, series.seasons],
  )

  const started = batch.items.length > 0
  const hasTicked = ticked.size > 0
  // P6-293: "Link only" (and the no-ticks primary button, which already
  // sends this same subset) — the chain's links alone. A filter of what
  // `buildBatchAsks` already computed, never a second ask-builder.
  const linkOnlyAsks = asks.filter((ask) => ask.kind === KIND_LINK_SEQUEL)

  const toggleSeason = (seasonId) => {
    setTicked((previous) => {
      const next = new Set(previous)
      if (next.has(seasonId)) next.delete(seasonId)
      else next.add(seasonId)
      return next
    })
  }

  if (series.status === SERIES_LOADING) {
    return (
      <p className="d2d-micro d2d-serieschecklist__loading" data-testid={`${testId}-loading`}>
        Loading the whole series…
      </p>
    )
  }

  if (series.status === SERIES_SIGNED_OUT) {
    return (
      <p className="d2d-serieschecklist__notice" data-testid={`${testId}-signed-out`}>
        You need to be signed in to do that.{' '}
        <a href={signInHref(location)}>Sign in with Discord</a>.
      </p>
    )
  }

  if (series.status === SERIES_NOT_FOUND) {
    return (
      <p className="d2d-serieschecklist__notice" role="alert" data-testid={`${testId}-not-found`}>
        {series.message}
      </p>
    )
  }

  if (series.status === SERIES_LIMITED) {
    return (
      <p className="d2d-serieschecklist__notice" role="alert" data-testid={`${testId}-limited`}>
        {series.message}
      </p>
    )
  }

  if (series.status === SERIES_ERROR) {
    return (
      <p className="d2d-serieschecklist__notice" role="alert" data-testid={`${testId}-error`}>
        {series.message}
      </p>
    )
  }

  const unsent = unsentCount(batch.items)
  const refusals = batch.items.filter((item) => item.status === REFUSED)
  // Two different reasons this checklist cannot clear any season yet
  // (round 2): the archive itself has not settled, or it settled READY but
  // predates `source` (a deploy-timing window -- see the module header).
  // Both read as "still checking" to the member; only an outright fetch
  // failure (UNAVAILABLE/MISCONFIGURED) reads as a harder "couldn't check."
  const archiveVerifiable = archive.state === READY && archiveSupportsSourceMatch(archiveTitles)
  const archiveFailed = archive.state === UNAVAILABLE || archive.state === MISCONFIGURED

  return (
    <div className="d2d-serieschecklist" data-testid={`${testId}-checklist`}>
      {!series.complete ? (
        <p className="d2d-micro d2d-serieschecklist__notice" data-testid={`${testId}-incomplete`}>
          Couldn’t fetch the whole series. Showing what we found.
        </p>
      ) : null}
      {!archiveVerifiable ? (
        <p className="d2d-micro d2d-serieschecklist__notice" data-testid={`${testId}-unverified`}>
          {archiveFailed
            ? 'Couldn’t check what you’ve already logged, so nothing here can be logged yet.'
            : 'Checking what you’ve already logged…'}
        </p>
      ) : null}

      <ul className="d2d-serieschecklist__rows">
        {series.seasons.map((season) => {
          const kind = rowKindFor(season, archive.state, archiveTitles, userId)
          // P6-293: only meaningful once `kind` is already `ROW_TICKABLE`
          // (which itself already required `archiveSupportsSourceMatch`) —
          // see `inProgressStatusForSeason`'s own header.
          const inProgress = kind === ROW_TICKABLE ? inProgressStatusForSeason(season, archiveTitles, userId) : null
          const rowTestId = `${testId}-row-${season.id}`
          return (
            <li key={season.id} className="d2d-serieschecklist__row" data-testid={rowTestId}>
              <PosterSlot subscription={{ poster_url: season.poster }} size="search" />
              <div className="d2d-serieschecklist__rowbody">
                <div className="d2d-serieschecklist__rowtitle">
                  {season.title}
                  {season.selected ? (
                    <span className="d2d-micro d2d-serieschecklist__selected" data-testid={`${rowTestId}-selected`}>
                      {' '}Searched
                    </span>
                  ) : null}
                </div>
                <div className="d2d-micro d2d-serieschecklist__rowmeta">
                  {typeof season.year === 'number' ? season.year : 'year unknown'}
                  {season.format ? ` · ${season.format}` : ''}
                </div>
                {started ? (
                  <div className="d2d-micro d2d-serieschecklist__itemstatus" data-testid={`${testId}-item-log-${season.id}`}>
                    {/* P6-298c: a run started by the whole-series button
                        keys its items `list:${id}`, never `log:${id}` -- a
                        batch never carries both for the same season, so
                        checking both keys here gives that button the same
                        per-row feedback the log flow already has, with no
                        new testid to learn. */}
                    {itemStatusText(
                      batch.items.find(
                        (item) => item.key === `log:${season.id}` || item.key === `list:${season.id}`,
                      ) ?? {},
                    )}
                  </div>
                ) : null}
              </div>
              {kind === ROW_TICKABLE ? (
                <>
                  {inProgress ? (
                    <span className="d2d-micro d2d-serieschecklist__rowstatus" data-testid={`${rowTestId}-status`}>
                      {inProgressWord(inProgress)}
                    </span>
                  ) : null}
                  <input
                    type="checkbox"
                    aria-label={`Log ${season.title}`}
                    checked={ticked.has(season.id)}
                    disabled={started}
                    onChange={() => toggleSeason(season.id)}
                    className="d2d-serieschecklist__checkbox"
                    data-testid={`${rowTestId}-checkbox`}
                  />
                </>
              ) : (
                <span className="d2d-micro d2d-serieschecklist__rowstatus" data-testid={`${rowTestId}-status`}>
                  {kind === ROW_LOGGED ? 'Logged' : kind === ROW_UNVERIFIED ? 'Can’t confirm yet' : statusWord(season.status)}
                </span>
              )}
            </li>
          )
        })}
      </ul>

      {series.seasons.length > 1 ? (
        <ul className="d2d-micro d2d-serieschecklist__links" data-testid={`${testId}-links`}>
          {series.seasons.slice(0, -1).map((from, i) => {
            const to = series.seasons[i + 1]
            const key = `link:${from.id}->${to.id}`
            const item = batch.items.find((candidate) => candidate.key === key)
            return (
              <li key={key} data-testid={`${testId}-item-${key}`}>
                {from.title} → {to.title}
                {started ? `: ${itemStatusText(item ?? {})}` : null}
              </li>
            )
          })}
        </ul>
      ) : null}

      {!started ? (
        <>
          {hasTicked ? (
            <p className="d2d-micro d2d-serieschecklist__nodate" data-testid={`${testId}-nodate`}>
              Logged without dates. Add a date on each title’s page.
            </p>
          ) : null}
          {/* P6-298c: "Add the whole series" needs neither a tick nor a
              settled archive (see `buildWholeSeriesAsks`'s own header), so
              `nothingAtAll` -- not `asks.length` alone -- is what decides
              between the button row and the "nothing to do" message. In
              practice `wholeSeriesAsks` is empty only when the chain itself
              is (defensive; `seasonsOf` already keeps that from reaching
              here), so the old message is effectively retired without
              deleting the fail-closed branch that used to show it. */}
          {asks.length === 0 && wholeSeriesAsks.length === 0 ? (
            <div className="d2d-serieschecklist__actions">
              <p className="d2d-micro" data-testid={`${testId}-nothing`}>
                Nothing here can be logged or linked right now.
              </p>
              <button
                type="button"
                className="d2d-serieschecklist__cancel"
                data-testid={`${testId}-cancel`}
                onClick={onClose}
              >
                Close
              </button>
            </div>
          ) : (
            <div className="d2d-serieschecklist__actions">
              {asks.length > 0 ? (
                <button
                  type="button"
                  className="d2d-serieschecklist__submit"
                  data-testid={`${testId}-submit`}
                  onClick={() => batch.run(asks)}
                >
                  {hasTicked ? 'Log & link the chain' : 'Link the chain'}
                </button>
              ) : null}
              {hasTicked && linkOnlyAsks.length > 0 ? (
                <button
                  type="button"
                  className="d2d-serieschecklist__cancel"
                  // P6-293: this file cannot touch app.css (out of scope --
                  // see the brief), and `.cancel`'s `white-space: nowrap` is
                  // sized for "Cancel"/"Close," not this button's longer
                  // label -- measured on the real-browser 320px viewport
                  // suite (`viewport.test.js`'s existing 12-season-chain
                  // case): unwrapped, this label alone pushed the whole
                  // page 10px past the edge. Wrapping it is the one knob
                  // available from inside this component.
                  style={{ whiteSpace: 'normal', textAlign: 'center' }}
                  data-testid={`${testId}-linkonly`}
                  onClick={() => batch.run(linkOnlyAsks)}
                >
                  Link only (nothing watched)
                </button>
              ) : null}
              {wholeSeriesAsks.length > 0 ? (
                <button
                  type="button"
                  className="d2d-serieschecklist__cancel"
                  // Same wrapping note as "Link only" above -- this label is
                  // longer still.
                  style={{ whiteSpace: 'normal', textAlign: 'center' }}
                  data-testid={`${testId}-addseries`}
                  onClick={() => batch.run(wholeSeriesAsks)}
                >
                  Add the whole series (nothing watched)
                </button>
              ) : null}
              <button
                type="button"
                className="d2d-serieschecklist__cancel"
                data-testid={`${testId}-cancel`}
                onClick={onClose}
              >
                Cancel
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="d2d-serieschecklist__summary" role="status" data-testid={`${testId}-summary`}>
          {finishedSummaryText(batch.items)}
        </div>
      )}

      {started && refusals.length > 0 ? (
        <ul className="d2d-micro d2d-serieschecklist__refusals" data-testid={`${testId}-refusals`}>
          {refusals.map((item) => (
            <li key={item.key} data-testid={`${testId}-refusal-${item.key}`}>
              {item.label} — {actionSentence(REFUSED, item.message)}
            </li>
          ))}
        </ul>
      ) : null}

      {started && unsent > 0 ? (
        <div className="d2d-serieschecklist__unsent" role="alert" data-testid={`${testId}-toomany`}>
          <p className="d2d-micro">Too many at once. Try the rest in a minute.</p>
          <button
            type="button"
            className="d2d-serieschecklist__retry"
            data-testid={`${testId}-retry-unsent`}
            onClick={() => batch.retryUnsent()}
          >
            {`Try the rest (${unsent})`}
          </button>
        </div>
      ) : null}
    </div>
  )
}

/**
 * The Add panel's own toggle for one AniList search result — brief §2.2:
 * "On every AniList search result, a 'Log the whole series' control...
 * It opens the checklist for that result's id." Self-contained open/closed
 * state, `LogItControl`'s own shape (a toggle button when closed, the real
 * control when open) rather than a second pattern for the identical idea.
 *
 * `hit` is one `/api/watch/search` result; only ever mounted for an AniList
 * hit (`SearchResultRow`'s own gate, `AddPanel.jsx`) — TMDB results don't
 * get this control at all (brief §2.2: "a TMDB show is already one title
 * with all its seasons").
 */
export function SeriesChecklist({ hit, testId = 'search-series' }) {
  const [open, setOpen] = useState(false)

  if (!open) {
    return (
      <button
        type="button"
        aria-expanded={false}
        className="d2d-serieschecklist__toggle"
        data-testid={`${testId}-toggle`}
        onClick={() => setOpen(true)}
      >
        Log the whole series
      </button>
    )
  }

  return <SeriesChecklistBody anilistId={hit.id} testId={testId} onClose={() => setOpen(false)} />
}

export default SeriesChecklist
